import React, { useState, useRef } from 'react'
import ReactCrop from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { PurpleButton } from 'components/shared/buttons/Button'
import Button from 'components/shared/buttons/Button'
import styles from './team-banner-cropper.module.scss'
import { initialBannerCrop } from './bannerCrop'

export default function TeamBannerCropper({ image, onSave, onCancel }) {
    const [crop, setCrop] = useState(
        /** @type {import('react-image-crop').Crop | undefined} */ (undefined),
    )
    const [completedCrop, setCompletedCrop] = useState(
        /** @type {import('react-image-crop').PixelCrop | undefined} */ (undefined),
    )
    const [loading, setLoading] = useState(false)
    const imgRef = useRef(null)

    const onImageLoad = e => {
        const { width, height } = e.currentTarget
        const initialCrop = initialBannerCrop(width, height)

        setCrop(initialCrop)
        // ReactCrop normally derives this through onComplete after layout. Keeping
        // it in sync here makes the default crop immediately saveable even when
        // that callback is skipped (for example while opening inside a modal).
        setCompletedCrop(initialCrop)
    }

    const getCroppedImg = async () => {
        const image = imgRef.current
        const crop = completedCrop

        if (!image || !crop || !crop.width || !crop.height) {
            throw new Error('Crop area not properly defined')
        }

        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
            throw new Error('No 2d context')
        }

        const scaleX = image.naturalWidth / image.width
        const scaleY = image.naturalHeight / image.height

        // Set real canvas size
        canvas.width = 1200
        canvas.height = 400

        // Fill with white background first
        ctx.fillStyle = '#FFFFFF'
        ctx.fillRect(0, 0, canvas.width, canvas.height)

        // Set canvas color profile and rendering settings
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'

        // Draw cropped image
        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            canvas.width,
            canvas.height,
        )

        return new Promise((resolve, reject) => {
            canvas.toBlob(blob => {
                if (!blob) {
                    reject(new Error('Canvas is empty'))
                    return
                }
                resolve(blob)
            }, 'image/png')
        })
    }

    const handleSave = async () => {
        setLoading(true)
        try {
            const croppedBlob = await getCroppedImg()
            await onSave(croppedBlob)
        } catch (e) {
            console.error('Failed to crop image:', e)
            alert('Failed to crop image. Please try again.')
        }
        setLoading(false)
    }

    return (
        <div className={styles.cropperOverlay}>
            <div className={styles.cropperModal}>
                <h3>Crop Banner Image</h3>
                <div className={styles.cropperContainer}>
                    <ReactCrop
                        crop={crop}
                        onChange={(_, percentCrop) => setCrop(percentCrop)}
                        onComplete={c => setCompletedCrop(c)}
                        aspect={3 / 1}
                        keepSelection
                    >
                        <img
                            ref={imgRef}
                            src={image}
                            alt="Crop me"
                            onLoad={onImageLoad}
                            style={{ maxWidth: '100%', maxHeight: '400px' }}
                        />
                    </ReactCrop>
                </div>
                <div className={styles.actions}>
                    <Button text="Cancel" onClick={onCancel} />
                    <PurpleButton
                        text={loading ? 'Saving...' : 'Save Banner'}
                        onClick={handleSave}
                        disabled={loading || !completedCrop}
                    />
                </div>
            </div>
        </div>
    )
}
